import type {TextureSetSockets} from "#material/nodes/texture/texture-set/definition"
import {textureSetDefinition} from "#material/nodes/texture/texture-set/definition"
import {registerMaterialGraphNode} from "#material/register"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import {createMappingNode, createTexImageNodeForNodeImageResource} from "#material/nodes/texture/texture-set/helpers"
import {NormalMap} from "#material/nodes/vector/normal-map/normal-map"
import {VectorMath} from "#material/nodes/converter/vector-math/vector-math"
import {RgbCurve} from "#material/nodes/color/rgb-curve/rgb-curve"
import {Displacement} from "#material/nodes/vector/displacement/displacement"
import {ScalarMath} from "#material/nodes/converter/scalar-math/scalar-math"
import {TexCoord} from "#material/nodes/texture/tex-coord/tex-coord"
import {SeparateXyz} from "#material/nodes/converter/separate-xyz/separate-xyz"
import {SeparateRgb} from "#material/nodes/converter/separate-rgb/separate-rgb"
import {SeparateHsv} from "#material/nodes/converter/separate-hsv/separate-hsv"
import {RgbToBw} from "#material/nodes/converter/rgb-to-bw/rgb-to-bw"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {TextureType} from "#material/types/texture"
import {materialConnection} from "#material/nodes/base/connection/shorthand"
import type {DataResolver} from "#material/validation/data-resolver/index"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import type {InputMaterialConnection, OutputMaterialConnection} from "#material/sockets/definition"
import {rotateNormalMapVector} from "#material/helpers/rotate-normal-map-vector"
import {value} from "#material/nodes/input/value/shorthand"
import {color} from "#material/nodes/input/color/shorthand"
import {combineXyz} from "#material/nodes/converter/combine-xyz/shorthand"
import type {MaterialNodeFlattenedReturns} from "#material/sockets/parameters"

@registerMaterialGraphNode
export class TextureSet extends MaterialNode<TextureSetSockets> {
    public override get definition() {
        return textureSetDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<TextureSetSockets>> => {
        const graph = await this.buildTextureSetGraph(evaluator.context.resolve)
        return evaluator.getAll(graph)
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<TextureSetSockets>> => {
        const graph = await this.buildTextureSetGraph(evaluator.context.resolve)
        return evaluator.getAll(graph)
    }

    override flatten = async (resolve: DataResolver) => {
        return this.buildTextureSetGraph(resolve)
    }

    async buildTextureSetGraph(resolve: DataResolver): Promise<MaterialNodeFlattenedReturns<TextureSetSockets>> {
        const {settings, inputs} = this.parameters
        const {textureSetRevisionId, normalStrength} = settings

        const inNormalStrength = inputs.normalStrength?.clone({cloneSubNode: () => false}) ?? value(normalStrength ?? 1)

        const createDefaultUV = () => materialConnection(new TexCoord({parameters: {inputs: {}, settings: {}}}), "uv")
        const createDefaultBaseColor = () => color({r: 0.5, g: 0.5, b: 0.5})
        const createDefaultMetallic = () => value(0)
        const createDefaultSpecular = () => value(0.5)
        const createDefaultRoughness = () => value(1)
        const createDefaultAnisotropic = () => value(0)
        const createDefaultAnisotropicRotation = () => value(0)
        const createDefaultAlpha = () => value(1)
        const createDefaultNormal = () =>
            materialConnection(
                new NormalMap({
                    parameters: {inputs: {strength: inNormalStrength.clone({cloneSubNode: () => false}), color: color({r: 0.5, g: 0.5, b: 1})}, settings: {}},
                }),
                "normal",
            )
        const createDefaultDisplacement = () => combineXyz(0, 0, 0)
        const createDefaultTransmission = () => color({r: 0, g: 0, b: 0})

        const defaultValues = {
            baseColor: createDefaultBaseColor(),
            metallic: createDefaultMetallic(),
            specular: createDefaultSpecular(),
            roughness: createDefaultRoughness(),
            anisotropic: createDefaultAnisotropic(),
            anisotropicRotation: createDefaultAnisotropicRotation(),
            alpha: createDefaultAlpha(),
            normal: createDefaultNormal(),
            displacement: createDefaultDisplacement(),
            transmission: createDefaultTransmission(),
        }

        if (textureSetRevisionId === undefined) {
            return defaultValues
        }

        const textureSetRevision = await resolve.textureSetRevision(textureSetRevisionId)
        if (!textureSetRevision) {
            throw new Error("Texture set not found")
        }
        const {widthCm, heightCm, displacementCm} = textureSetRevision
        if (widthCm === undefined || heightCm === undefined) {
            return defaultValues
        }

        const mapping = materialConnection(
            createMappingNode(inputs.uv?.clone({cloneSubNode: () => false}) ?? createDefaultUV(), [widthCm, heightCm, 1]),
            "vector",
        )

        const createAnisotropicFromAnisotropy = (colorMassAnisotropy: InputMaterialConnection<"color"> | undefined) => {
            if (!colorMassAnisotropy) {
                return undefined
            }

            return materialConnection(
                new VectorMath({
                    parameters: {
                        inputs: {
                            vector: materialConnection(
                                new VectorMath({
                                    parameters: {
                                        inputs: {
                                            vector: materialConnection(
                                                new VectorMath({
                                                    parameters: {
                                                        inputs: {
                                                            vector: colorMassAnisotropy,
                                                        },
                                                        settings: {
                                                            operation: "ADD",
                                                            vector_001: {x: -0.5, y: -0.5, z: 0},
                                                        },
                                                    },
                                                }),
                                                "vector",
                                            ),
                                        },
                                        settings: {
                                            operation: "MULTIPLY",
                                            vector_001: {x: 2, y: 2, z: 0},
                                        },
                                    },
                                }),
                                "vector",
                            ),
                        },
                        settings: {
                            operation: "LENGTH",
                        },
                    },
                }),
                "value",
            )
        }

        const createAnisotropicRotationFromAnisotropy = (colorMassAnisotropy: InputMaterialConnection<"color"> | undefined) => {
            if (!colorMassAnisotropy) {
                return undefined
            }

            const xyz = new SeparateXyz({
                parameters: {
                    inputs: {
                        vector: materialConnection(
                            new VectorMath({
                                parameters: {
                                    inputs: {
                                        vector: materialConnection(
                                            new VectorMath({
                                                parameters: {
                                                    inputs: {
                                                        vector: colorMassAnisotropy,
                                                    },
                                                    settings: {
                                                        operation: "ADD",
                                                        vector_001: {x: -0.5, y: -0.5, z: 0},
                                                    },
                                                },
                                            }),
                                            "vector",
                                        ),
                                    },
                                    settings: {
                                        operation: "MULTIPLY",
                                        vector_001: {x: 2, y: 2, z: 0},
                                    },
                                },
                            }),
                            "vector",
                        ),
                    },
                    settings: {},
                },
            })

            const angle = materialConnection(
                new ScalarMath({
                    parameters: {
                        inputs: {
                            value: materialConnection(xyz, "y"),
                            value_001: materialConnection(xyz, "x"),
                        },
                        settings: {operation: "ARCTAN2"},
                    },
                }),
                "value",
            )

            return materialConnection(
                new ScalarMath({
                    parameters: {
                        inputs: {
                            value: angle,
                        },
                        settings: {operation: "DIVIDE", value_001: Math.PI * 4},
                    },
                }),
                "value",
            )
        }

        const convertLegacyAnisotropyRotation = (sourceMapAnisotropyRotation: InputMaterialConnection<"color"> | undefined) => {
            if (!sourceMapAnisotropyRotation) {
                return undefined
            }
            return materialConnection(
                new ScalarMath({
                    parameters: {
                        inputs: {
                            value_001: sourceMapAnisotropyRotation,
                        },
                        settings: {operation: "SUBTRACT", value: 1.0},
                    },
                }),
                "value",
            )
        }

        const convertNormal = (normalMap: InputMaterialConnection<"color"> | undefined, normalRotation: number) => {
            if (!normalMap) {
                return undefined
            }

            const normalColor = materialConnection(
                new RgbCurve({
                    parameters: {
                        inputs: {
                            color: normalMap,
                        },
                        settings: {
                            fac: 1,
                            ...RgbCurve.settingsFromControlPoints([
                                [
                                    {x: 0, y: 0},
                                    {x: 1, y: 1},
                                ], // R
                                [
                                    {x: 0, y: 1},
                                    {x: 1, y: 0},
                                ], // G (inverted)
                                [
                                    {x: 0, y: 0},
                                    {x: 1, y: 1},
                                ], // B
                                [
                                    {x: 0, y: 0},
                                    {x: 1, y: 1},
                                ], // RGB
                            ]),
                        },
                    },
                }),
                "color",
            )

            return materialConnection(
                new NormalMap({
                    parameters: {
                        inputs: {
                            color: rotateNormalMapVector(normalColor, normalRotation),
                            strength: inNormalStrength.clone({cloneSubNode: () => false}),
                        },
                        settings: {},
                    },
                }),
                "normal",
            )
        }

        const convertDisplacement = (displacementMap: InputMaterialConnection<"color"> | undefined) => {
            if (!displacementMap) {
                return undefined
            }

            return materialConnection(
                new Displacement({
                    parameters: {
                        inputs: {
                            height: displacementMap,
                            scale: materialConnection(
                                new ScalarMath({
                                    parameters: {
                                        inputs: {
                                            value: inNormalStrength.clone({cloneSubNode: () => false}),
                                        },
                                        settings: {
                                            value_001: displacementCm ?? 0,
                                            operation: "MULTIPLY",
                                        },
                                    },
                                }),
                                "value",
                            ),
                        },
                        settings: {midlevel: 0.5},
                    },
                }),
                "displacement",
            )
        }

        const createMetallicFromF0 = (colormassF0: InputMaterialConnection<"color"> | undefined) => {
            if (!colormassF0) {
                return undefined
            }

            return materialConnection(
                new SeparateRgb({
                    parameters: {
                        inputs: {
                            image: colormassF0,
                        },
                        settings: {},
                    },
                }),
                "g",
            )
        }

        const createSpecularFromF0 = (colormassF0: InputMaterialConnection<"color"> | undefined) => {
            if (!colormassF0) {
                return undefined
            }

            return materialConnection(
                new SeparateRgb({
                    parameters: {
                        inputs: {
                            image: colormassF0,
                        },
                        settings: {},
                    },
                }),
                "r",
            )
        }

        const createAnisotropicFromAnisotropyStrength = (colorMassAnisotropyStrength: InputMaterialConnection<"color"> | undefined) => {
            if (!colorMassAnisotropyStrength) {
                return undefined
            }

            return materialConnection(
                new ScalarMath({
                    parameters: {
                        inputs: {
                            value: materialConnection(
                                new ScalarMath({
                                    parameters: {
                                        inputs: {value: colorMassAnisotropyStrength},
                                        settings: {operation: "ADD", value_001: -0.5},
                                    },
                                }),
                                "value",
                            ),
                        },
                        settings: {
                            operation: "MULTIPLY",
                            value_001: 2,
                        },
                    },
                }),
                "value",
            )
        }

        const createAlphaFromTransmission = (
            transmissionMap: InputMaterialConnection<"color"> | undefined,
            minValue: number,
            maxValue: number,
            useLuminance = false,
        ) => {
            if (!transmissionMap || minValue >= maxValue) {
                return undefined
            }
            // rescale and invert ([minValue...maxValue] -> [1...0])
            return materialConnection(
                new ScalarMath({
                    parameters: {
                        inputs: {
                            value: materialConnection(
                                new ScalarMath({
                                    parameters: {
                                        inputs: {
                                            value: useLuminance
                                                ? materialConnection(
                                                      new RgbToBw({
                                                          parameters: {
                                                              inputs: {
                                                                  color: transmissionMap,
                                                              },
                                                              settings: {},
                                                          },
                                                      }),
                                                      "val",
                                                  )
                                                : materialConnection(
                                                      new SeparateHsv({
                                                          parameters: {
                                                              inputs: {
                                                                  image: transmissionMap,
                                                              },
                                                              settings: {},
                                                          },
                                                      }),
                                                      "v",
                                                  ),
                                        },
                                        settings: {
                                            operation: "ADD",
                                            value_001: -maxValue,
                                            useClamp: false,
                                        },
                                    },
                                }),
                                "value",
                            ),
                        },
                        settings: {
                            operation: "MULTIPLY",
                            value_001: 1 / (minValue - maxValue),
                            useClamp: true,
                        },
                    },
                }),
                "value",
            )
        }

        const transmissionMin = settings.transmissionMin ?? 0
        const transmissionMax = settings.transmissionMax ?? 1

        const mapAssignments = textureSetRevision.mapAssignments

        // current set
        const sourceMapDiffuse = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.Diffuse,
            mapping.clone({cloneSubNode: () => false}),
        )
        const sourceMapMetalness = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.Metalness,
            mapping.clone({cloneSubNode: () => false}),
        )
        const sourceMapSpecularStrength = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.SpecularStrength,
            mapping.clone({cloneSubNode: () => false}),
        )
        const sourceMapRoughness = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.Roughness,
            mapping.clone({cloneSubNode: () => false}),
        )
        const sourceMapAnisotropy = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.Anisotropy,
            mapping.clone({cloneSubNode: () => false}),
        )
        const sourceMapNormal = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.Normal,
            mapping.clone({cloneSubNode: () => false}),
        )
        const sourceMapDisplacement = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.Displacement,
            mapping.clone({cloneSubNode: () => false}),
            "Cubic",
        )
        // legacy set
        const sourceMapF0 = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.F0,
            mapping.clone({cloneSubNode: () => false}),
        )
        const sourceMapAnisotropyStrength = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.AnisotropyStrength,
            mapping.clone({cloneSubNode: () => false}),
        )
        const sourceMapAnisotropyRotation = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.AnisotropyRotation,
            mapping.clone({cloneSubNode: () => false}),
        )
        // special maps
        const sourceMapMask = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.Mask,
            mapping.clone({cloneSubNode: () => false}),
        )
        const sourceMapTransmission = createTexImageNodeForNodeImageResource(
            textureSetRevision.id,
            mapAssignments,
            TextureType.Transmission,
            mapping.clone({cloneSubNode: () => false}),
        )

        const normalRotation = settings.normalRotation ?? 0

        // create the nodes
        const baseColor = sourceMapDiffuse ?? createDefaultBaseColor()
        const metallic = sourceMapMetalness ?? createMetallicFromF0(sourceMapF0) ?? createDefaultMetallic()
        const specular = sourceMapSpecularStrength ?? createSpecularFromF0(sourceMapF0) ?? createDefaultSpecular()
        const roughness = sourceMapRoughness ?? createDefaultRoughness()
        const anisotropic =
            createAnisotropicFromAnisotropy(sourceMapAnisotropy?.clone({cloneSubNode: () => false})) ??
            createAnisotropicFromAnisotropyStrength(sourceMapAnisotropyStrength) ??
            createDefaultAnisotropic()
        const anisotropicRotation =
            createAnisotropicRotationFromAnisotropy(sourceMapAnisotropy?.clone({cloneSubNode: () => false})) ??
            convertLegacyAnisotropyRotation(sourceMapAnisotropyRotation) ??
            createDefaultAnisotropicRotation()
        const alpha = sourceMapMask ?? createAlphaFromTransmission(sourceMapTransmission, transmissionMin, transmissionMax) ?? createDefaultAlpha()
        const normal = convertNormal(sourceMapNormal, normalRotation) ?? createDefaultNormal()
        const displacement = convertDisplacement(sourceMapDisplacement) ?? createDefaultDisplacement()
        const transmission = sourceMapTransmission ?? createDefaultTransmission()

        return {
            baseColor,
            metallic: metallic as OutputMaterialConnection<"float">,
            specular: specular as OutputMaterialConnection<"float">,
            roughness: roughness as OutputMaterialConnection<"float">,
            anisotropic,
            anisotropicRotation,
            alpha: alpha as OutputMaterialConnection<"float">,
            normal,
            displacement,
            transmission,
        }
    }

    override applyDefaults() {
        super.applyDefaults()
        if (this.parameters.settings.transmissionMin === undefined) this.updateSetting("transmissionMin", 0.2)
        if (this.parameters.settings.transmissionMax === undefined) this.updateSetting("transmissionMax", 0.9)
    }

    public async sortedMapAssignments(resolve: DataResolver) {
        const textureSetRevisionId = this.parameters.settings.textureSetRevisionId
        if (!textureSetRevisionId) {
            return []
        }
        const textureSetRevision = await resolve.textureSetRevision(textureSetRevisionId)
        if (!textureSetRevision) {
            return []
        }
        return Object.entries(textureSetRevision.mapAssignments ?? {}).sort(([a], [b]) => a.localeCompare(b))
    }
}
